(function (exports) {
  "use strict";

  const scaleUpAndAnimateBackgroundFromLeftToRight = (
    element,
    animationDuration
  ) => {
    const originalBackgroundSize = element.style.backgroundSize;
    const originalBackgroundImage = element.style.backgroundImage;
    element.style.backgroundSize = "200% 100%";
    element.style.backgroundImage =
      "linear-gradient(90deg, #3f87a655 50%, #0000 50%)";
    element.style.backgroundClip = "padding-box";
    const resetElementStyles = () => {
      element.style.backgroundImage = originalBackgroundImage;
      element.style.backgroundSize = originalBackgroundSize;
    };
    const animation = element.animate(
      [
        {
          transform: "scale(1)",
          backgroundPosition: "100% 0%",
        },
        {
          transform: "scale(1.05)",
          backgroundPosition: "100% 0%",
          offset: 0.1,
        },
        {
          transform: "scale(1.05)",
          backgroundPosition: "0% 0%",
        },
      ],
      {
        duration: animationDuration,
        iterations: 1,
        easing: "linear",
        fill: "none",
      }
    );
    animation.addEventListener("finish", resetElementStyles);
    animation.addEventListener("cancel", resetElementStyles);
    animation.addEventListener("remove", resetElementStyles);
    return animation;
  };

  const v0 = {
    events: {
      gazeOver: "tdxFocus",
      gazeOut: "tdxUnfocus",
      gazeActivate: "tdxActivate",
    },
    attributes: {
      gazeRegion: "data-tdx-interactor",
      dwellTime: "data-tdx-dwell-time",
    },
  };
  const v1 = {
    events: {
      gazeOver: "gazeover",
      gazeOut: "gazeout",
      gazeActivate: "gazeactivate",
    },
    attributes: {
      gazeRegion: "data-gaze-region",
      dwellTime: "data-gaze-region-dwell-time",
    },
  };
  const defaultOptions = {
    dwellTime: 1000,
    activationTime: 200,
    activateClickOnGazeActivation: true,
    useDwellAnimation: true,
  };
  const GazableHTMLElement = (superClass, options = {}) => {
    class GazableElementClass extends superClass {
      _activateTimeout = undefined;
      _dwellAnimation = undefined;
      _simulateGaze = false;
      _defaults = { ...defaultOptions, ...options };
      constructor(...args) {
        super(...args);
      }
      connectedCallback() {
        this.gazeInteractable = true;
        this.style.setProperty("--dwell-time", `${this.dwellTime}ms`);
        this.style.setProperty(
          "--activation-animation-time",
          `${this.activationAnimationTime}ms`
        );
        this._simulateGaze =
          new URLSearchParams(window.location.search).get("simulategaze") !==
          null;
        if (this._simulateGaze) {
          console.info("simulating gaze using mouse");
          this.addEventListener("mouseover", this.#onGazeOver, {
            capture: true,
            passive: true,
          });
          this.addEventListener("mouseout", this.#onGazeOut, {
            capture: true,
            passive: true,
          });
        } else {
          this.addEventListener(v0.events.gazeOver, this.#onGazeOver);
          this.addEventListener(v0.events.gazeOut, this.#onGazeOut);
          this.addEventListener(v0.events.gazeActivate, this.#onGazeActivate);
          this.addEventListener(v1.events.gazeOver, this.#onGazeOver);
          this.addEventListener(v1.events.gazeOut, this.#onGazeOut);
          this.addEventListener(v1.events.gazeActivate, this.#onGazeActivate);
        }
        this.addEventListener("click", this.#onClick, {
          capture: true,
          passive: true,
        });
      }
      disconnectedCallback() {
        if (this._simulateGaze) {
          this.removeEventListener("mouseover", this.#onGazeOver);
          this.removeEventListener("mouseout", this.#onGazeOut);
        } else {
          this.removeEventListener(v0.events.gazeOver, this.#onGazeOver);
          this.removeEventListener(v0.events.gazeOut, this.#onGazeOut);
          this.removeEventListener(
            v0.events.gazeActivate,
            this.#onGazeActivate
          );
          this.removeEventListener(v1.events.gazeOver, this.#onGazeOver);
          this.removeEventListener(v1.events.gazeOut, this.#onGazeOut);
          this.removeEventListener(
            v1.events.gazeActivate,
            this.#onGazeActivate
          );
        }
        this.removeEventListener("click", this.#onClick);
        this.gazeInteractable = false;
        this._simulateGaze = false;
        this.style.removeProperty("--dwell-time");
        this.style.removeProperty("--activation-animation-time");
      }
      #onGazeOver = () => {
        if (this.hasAttribute("disabled")) return;
        this.setAttribute("gaze-focused", "");
        this.startDwellAnimation(this.dwellTime);
        if (this._simulateGaze) this.#startActivationSimulation();
      };
      #onGazeOut = () => {
        this.#removeGazeFocus();
      };
      #onGazeActivate = () => {
        if (this.hasAttribute("disabled")) return;
        if (this._defaults.activateClickOnGazeActivation) this.click();
      };
      #onClick = (e) => {
        if (e.target === this) {
          this.onActivate();
        }
      };
      #removeGazeFocus() {
        this.removeAttribute("gaze-focused");
        this.stopDwellAnimation();
        if (this._simulateGaze) this.#stopActivationSimulation();
      }
      #startActivationSimulation() {
        if (this._activateTimeout) clearTimeout(this._activateTimeout);
        this._activateTimeout = window.setTimeout(() => {
          this.#onGazeActivate();
          this._activateTimeout = undefined;
        }, this.dwellTime);
      }
      #stopActivationSimulation() {
        if (this._activateTimeout !== undefined) {
          window.clearTimeout(this._activateTimeout);
          this._activateTimeout = undefined;
        }
        this.blur();
      }
      onActivate() {
        this.#removeGazeFocus();
      }
      startDwellAnimation(_dwellTime) {
        if (this._defaults.useDwellAnimation) {
          if (this._dwellAnimation !== undefined) this._dwellAnimation.cancel();
          this._dwellAnimation = scaleUpAndAnimateBackgroundFromLeftToRight(
            this,
            this.dwellTime
          );
        } else {
          this.style.setProperty("--dwell-time", `${this.dwellTime}ms`);
        }
      }
      stopDwellAnimation() {
        if (this._defaults.useDwellAnimation) {
          this._dwellAnimation?.cancel();
          this._dwellAnimation = undefined;
        }
      }
      hasGazeFocus() {
        return this.hasAttribute("gaze-focused");
      }
      get gazeInteractable() {
        return (
          this.hasAttribute(v1.attributes.gazeRegion) ||
          this.hasAttribute(v0.attributes.gazeRegion)
        );
      }
      set gazeInteractable(val) {
        if (val) {
          this.setAttribute(v1.attributes.gazeRegion, "");
          this.setAttribute(v0.attributes.gazeRegion, "");
        } else {
          this.removeAttribute(v1.attributes.gazeRegion);
          this.removeAttribute(v0.attributes.gazeRegion);
        }
      }
      get dwellTime() {
        const dwellTime = Number.parseInt(
          this.getAttribute(v1.attributes.dwellTime) ||
            this.getAttribute(v0.attributes.dwellTime)
        );
        if (!Number.isNaN(dwellTime)) {
          if (dwellTime <= 0)
            console.error(
              `'${v1.attributes.dwellTime}' must be larger than zero`
            );
          return dwellTime;
        }
        return (
          window.eyeTracking?.getSystemSettings().dwellTime ||
          this._defaults.dwellTime
        );
      }
      set dwellTime(value) {
        if (!Number.isInteger(value)) {
          console.error(`Invalid dwell time value ${value}`);
          return;
        }
        this.setAttribute(v1.attributes.dwellTime, value.toString());
        this.setAttribute(v0.attributes.dwellTime, value.toString());
      }
      get activationAnimationTime() {
        const activationAnimationTime = Number.parseInt(
          this.getAttribute("activation-animation-time")
        );
        if (!Number.isNaN(activationAnimationTime)) {
          if (activationAnimationTime <= 0)
            console.error(
              `'activation-animation-time' must be larger than zero`
            );
          return activationAnimationTime;
        }
        return (
          window.eyeTracking?.getSystemSettings().activationTime ||
          this._defaults.activationTime
        );
      }
      set activationAnimationTime(value) {
        if (!Number.isInteger(value)) {
          console.error(`Invalid activation animation time value ${value}`);
          return;
        }
        this.setAttribute("activation-animation-time", value.toString());
      }
    }
    return GazableElementClass;
  };

  const GazableElement = GazableHTMLElement(HTMLElement, {
    useDwellAnimation: false,
  });
  if (!customElements.get("gazable-element")) {
    customElements.define("gazable-element", GazableElement);
  }

  var htmlTemplate =
    '<style>\r\n   :host {\r\n      display: inline-block;\r\n      border: var(--button-border, 1px solid transparent);\r\n      position: relative;\r\n      border-radius: var(--button-border-radius, 3pt);\r\n      box-sizing: border-box;\r\n      width: var(--button-width, 13.5vw);\r\n      height: var(--button-height, 5.4vw);\r\n      background-color: var(--button-dwell-background-color, rgba(56, 56, 56, 0.5));\r\n      padding: var(--button-dwell-thickness, 0.405vw); /* $square-button-height * 0.075; */\r\n      -webkit-tap-highlight-color: transparent;\r\n      user-select: none;\r\n   }\r\n\r\n   :host([disabled]) {\r\n      cursor: default;\r\n      pointer-events: none;\r\n      opacity: var(--button-disabled-opacity, 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) {\r\n      border-radius: var(--button-focused-border-radius, 0);\r\n      background: linear-gradient(\r\n         to left,\r\n         var(--button-dwell-background-color, rgba(56, 56, 56, 0.5)) 50%,\r\n         var(--button-dwell-color, #92bcff) 50%\r\n      );\r\n      background-size: 200% 200%;\r\n      background-repeat: repeat-y;\r\n      background-position: right bottom;\r\n   }\r\n\r\n   :host([gaze-activated]) {\r\n      border-radius: var(--button-border-radius, 3pt);\r\n   }\r\n\r\n   .inner-content {\r\n      display: flex;\r\n      justify-content: center;\r\n      align-items: center;\r\n      color: var(--button-text-color, #fff);\r\n      font-size: var(--button-font-size, 1vw);\r\n      font-weight: var(--button-font-weight, bold);\r\n      width: 100%;\r\n      height: 100%;\r\n      border-radius: var(--button-border-radius, 3px);\r\n      box-sizing: border-box;\r\n      font-family: var(--button-font-family, sans-serif);\r\n      box-shadow: var(\r\n         --button-inner-box-shadow,\r\n         inset 0px 0px 1px var(--button-inner-border-width, 1px) var(--button-inner-border-color, #fff)\r\n      );\r\n      background-color: var(--button-inner-background-color, #222);\r\n      text-align: center;\r\n      transition: box-shadow 200ms ease-out, border-radius 200ms ease-out;\r\n   }\r\n\r\n   :host([disabled]) .inner-content {\r\n      border-color: transparent;\r\n      background-color: var(--button-disabled-inner-color, #383838);\r\n      color: var(--button-disabled-text-color, #989899);\r\n   }\r\n\r\n   :host([gaze-activated]) .inner-content {\r\n      color: var(--button-activated-text-color, #111);\r\n      border-color: transparent;\r\n      box-shadow: var(\r\n            --button-activated-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         rgba(var(--button-activation-color-rgb, 146, 188, 255), 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) .inner-content,\r\n   :host(:hover) .inner-content {\r\n      border-color: var(--button-focused-inner-border-color, #92bcff);\r\n      box-shadow: var(\r\n            --button-focused-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         var(--button-focused-border-color, #92bcff);\r\n      border-radius: var(--button-focused-inner-border-radius, 0);\r\n   }\r\n\r\n   .inner-content slot {\r\n      height: 100%;\r\n      width: 100%;\r\n      display: flex;\r\n      align-items: center;\r\n      justify-content: center;\r\n   }\r\n</style>\r\n\r\n<div class="inner-content">\r\n   <slot></slot>\r\n</div>\r\n';

  var htmlTemplate2 =
    '<style>\r\n   :host {\r\n      display: inline-block;\r\n      border: var(--button-border, 1px solid transparent);\r\n      position: relative;\r\n      border-radius: var(--button-border-radius, 3pt);\r\n      box-sizing: border-box;\r\n      width: var(--button-width, 7.5vw);\r\n      height: var(--button-height, 5.4vw);\r\n      background-color: var(--button-dwell-background-color, rgba(56, 56, 56, 0.5));\r\n      padding: var(--button-dwell-thickness, 0.405vw); /* $square-button-height * 0.075; */\r\n      -webkit-tap-highlight-color: transparent;\r\n      user-select: none;\r\n   }\r\n\r\n   :host([disabled]) {\r\n      cursor: default;\r\n      pointer-events: none;\r\n      opacity: var(--button-disabled-opacity, 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) {\r\n      border-radius: var(--button-focused-border-radius, 0);\r\n      background: linear-gradient(\r\n         to left,\r\n         var(--button-dwell-background-color, rgba(56, 56, 56, 0.5)) 50%,\r\n         var(--button-dwell-color, #92bcff) 50%\r\n      );\r\n      background-size: 200% 200%;\r\n      background-repeat: repeat-y;\r\n      background-position: right bottom;\r\n   }\r\n\r\n   :host([gaze-activated]) {\r\n      border-radius: var(--button-border-radius, 3pt);\r\n   }\r\n\r\n   .inner-content {\r\n      display: flex;\r\n      justify-content: center;\r\n      align-items: center;\r\n      color: var(--button-text-color, #fff);\r\n      font-size: var(--button-font-size, 1vw);\r\n      font-weight: var(--button-font-weight, bold);\r\n      width: 100%;\r\n      height: 100%;\r\n      border-radius: var(--button-border-radius, 3px);\r\n      box-sizing: border-box;\r\n      font-family: var(--button-font-family, sans-serif);\r\n      box-shadow: var(\r\n         --button-inner-box-shadow,\r\n         inset 0px 0px 1px var(--button-inner-border-width, 1px) var(--button-inner-border-color, #fff)\r\n      );\r\n      background-color: var(--button-inner-background-color, #222);\r\n      text-align: center;\r\n      transition: box-shadow 200ms ease-out, border-radius 200ms ease-out;\r\n   }\r\n\r\n   :host([disabled]) .inner-content {\r\n      border-color: transparent;\r\n      background-color: var(--button-disabled-inner-color, #383838);\r\n      color: var(--button-disabled-text-color, #989899);\r\n   }\r\n\r\n   :host([gaze-activated]) .inner-content {\r\n      color: var(--button-activated-text-color, #111);\r\n      border-color: transparent;\r\n      box-shadow: var(\r\n            --button-activated-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         rgba(var(--button-activation-color-rgb, 146, 188, 255), 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) .inner-content,\r\n   :host(:hover) .inner-content {\r\n      border-color: var(--button-focused-inner-border-color, #92bcff);\r\n      box-shadow: var(\r\n            --button-focused-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         var(--button-focused-border-color, #92bcff);\r\n      border-radius: var(--button-focused-inner-border-radius, 0);\r\n   }\r\n\r\n   .inner-content slot {\r\n      height: 100%;\r\n      width: 100%;\r\n      display: flex;\r\n      align-items: center;\r\n      justify-content: center;\r\n   }\r\n</style>\r\n\r\n<div class="inner-content">\r\n   <slot></slot>\r\n</div>\r\n';

  const gazableSquareButtonTemplate = document.createElement("template");
  gazableSquareButtonTemplate.innerHTML = htmlTemplate;
  class GazableSquareButtonElement extends GazableHTMLElement(HTMLElement) {
    dwellAnimation = undefined;
    innerContent;
    activationColorRgb = "146, 188, 255";
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).append(
        gazableSquareButtonTemplate.content.cloneNode(true)
      );
      this.innerContent = this.shadowRoot?.querySelector(".inner-content");
    }
    startActivationAnimation() {
      const activationAnimation = this.animate(
        [
          { border: "0px" },
          {
            border:
              "var(--button-activation-border-width, 5px) solid transparent",
            backgroundColor: "transparent",
            offset: 0.5,
          },
          {
            border: "0px",
            backgroundColor: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
            offset: 0.75,
          },
          { backgroundColor: "transparent" },
        ],
        {
          duration: this.activationAnimationTime,
        }
      );
      this.setAttribute("gaze-activated", "");
      activationAnimation.oncancel = () =>
        this.removeAttribute("gaze-activated");
      activationAnimation.onfinish = () =>
        this.removeAttribute("gaze-activated");
      this.innerContent.animate(
        [
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 0.3)`,
            offset: 0.05,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
            offset: 0.4,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
          },
        ],
        {
          duration: this.activationAnimationTime,
          easing: "ease",
          fill: "backwards",
        }
      );
    }
    onActivate() {
      this.startActivationAnimation();
      super.onActivate();
    }
    startDwellAnimation(dwellTime) {
      if (this.dwellAnimation !== undefined) this.dwellAnimation.cancel();
      this.dwellAnimation = this.animate(
        [
          { backgroundPosition: "right bottom" },
          { backgroundPosition: "left bottom" },
        ],
        {
          duration: dwellTime,
          iterations: 1,
          easing: "linear",
          fill: "none",
        }
      );
    }
    stopDwellAnimation() {
      this.dwellAnimation?.cancel();
      this.dwellAnimation = undefined;
    }
  }
  if (!customElements.get("gazable-square-button")) {
    customElements.define("gazable-square-button", GazableSquareButtonElement);
  }

  var htmlTemplate2 =
    '<style>\r\n   :host {\r\n      display: inline-block;\r\n      border: var(--button-border, 1px solid transparent);\r\n      position: relative;\r\n      border-radius: var(--button-border-radius, 3pt);\r\n      box-sizing: border-box;\r\n      width: var(--button-width, 64px);\r\n      height: var(--button-height, 64px);\r\n      background-color: var(--button-dwell-background-color, rgba(56, 56, 56, 0.5));\r\n      padding: var(--button-dwell-thickness, 0.405vw); /* $square-button-height * 0.075; */\r\n      -webkit-tap-highlight-color: transparent;\r\n      user-select: none;\r\n   }\r\n\r\n   :host([disabled]) {\r\n      cursor: default;\r\n      pointer-events: none;\r\n      opacity: var(--button-disabled-opacity, 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) {\r\n      border-radius: var(--button-focused-border-radius, 0);\r\n      background: linear-gradient(\r\n         to left,\r\n         var(--button-dwell-background-color, rgba(56, 56, 56, 0.5)) 50%,\r\n         var(--button-dwell-color, #92bcff) 50%\r\n      );\r\n      background-size: 200% 200%;\r\n      background-repeat: repeat-y;\r\n      background-position: right bottom;\r\n   }\r\n\r\n   :host([gaze-activated]) {\r\n      border-radius: var(--button-border-radius, 3pt);\r\n   }\r\n\r\n   .inner-content {\r\n      display: flex;\r\n      justify-content: center;\r\n      align-items: center;\r\n      color: var(--button-text-color, #fff);\r\n      font-size: var(--button-font-size, 1vw);\r\n      font-weight: var(--button-font-weight, bold);\r\n      width: 100%;\r\n      height: 100%;\r\n      border-radius: var(--button-border-radius, 3px);\r\n      box-sizing: border-box;\r\n      font-family: var(--button-font-family, sans-serif);\r\n      box-shadow: var(\r\n         --button-inner-box-shadow,\r\n         inset 0px 0px 1px var(--button-inner-border-width, 1px) var(--button-inner-border-color, #fff)\r\n      );\r\n      background-color: var(--button-inner-background-color, #222);\r\n      text-align: center;\r\n      transition: box-shadow 200ms ease-out, border-radius 200ms ease-out;\r\n   }\r\n\r\n   :host([disabled]) .inner-content {\r\n      border-color: transparent;\r\n      background-color: var(--button-disabled-inner-color, #383838);\r\n      color: var(--button-disabled-text-color, #989899);\r\n   }\r\n\r\n   :host([gaze-activated]) .inner-content {\r\n      color: var(--button-activated-text-color, #111);\r\n      border-color: transparent;\r\n      box-shadow: var(\r\n            --button-activated-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         rgba(var(--button-activation-color-rgb, 146, 188, 255), 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) .inner-content,\r\n   :host(:hover) .inner-content {\r\n      border-color: var(--button-focused-inner-border-color, #92bcff);\r\n      box-shadow: var(\r\n            --button-focused-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         var(--button-focused-border-color, #92bcff);\r\n      border-radius: var(--button-focused-inner-border-radius, 0);\r\n   }\r\n\r\n   .inner-content slot {\r\n      height: 100%;\r\n      width: 100%;\r\n      display: flex;\r\n      align-items: center;\r\n      justify-content: center;\r\n   }\r\n</style>\r\n\r\n<div class="inner-content">\r\n   <slot></slot>\r\n</div>\r\n';

  const gazableSquareButton2Template = document.createElement("template");
  gazableSquareButton2Template.innerHTML = htmlTemplate2;
  class GazableSquareButtonTwoElement extends GazableHTMLElement(HTMLElement) {
    dwellAnimation = undefined;
    innerContent;
    activationColorRgb = "146, 188, 255";
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).append(
        gazableSquareButton2Template.content.cloneNode(true)
      );
      this.innerContent = this.shadowRoot?.querySelector(".inner-content");
    }
    startActivationAnimation() {
      const activationAnimation = this.animate(
        [
          { border: "0px" },
          {
            border:
              "var(--button-activation-border-width, 5px) solid transparent",
            backgroundColor: "transparent",
            offset: 0.5,
          },
          {
            border: "0px",
            backgroundColor: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
            offset: 0.75,
          },
          { backgroundColor: "transparent" },
        ],
        {
          duration: this.activationAnimationTime,
        }
      );
      this.setAttribute("gaze-activated", "");
      activationAnimation.oncancel = () =>
        this.removeAttribute("gaze-activated");
      activationAnimation.onfinish = () =>
        this.removeAttribute("gaze-activated");
      this.innerContent.animate(
        [
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 0.3)`,
            offset: 0.05,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
            offset: 0.4,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
          },
        ],
        {
          duration: this.activationAnimationTime,
          easing: "ease",
          fill: "backwards",
        }
      );
    }
    onActivate() {
      this.startActivationAnimation();
      super.onActivate();
    }
    startDwellAnimation(dwellTime) {
      if (this.dwellAnimation !== undefined) this.dwellAnimation.cancel();
      this.dwellAnimation = this.animate(
        [
          { backgroundPosition: "right bottom" },
          { backgroundPosition: "left bottom" },
        ],
        {
          duration: dwellTime,
          iterations: 1,
          easing: "linear",
          fill: "none",
        }
      );
    }
    stopDwellAnimation() {
      this.dwellAnimation?.cancel();
      this.dwellAnimation = undefined;
    }
  }
  if (!customElements.get("gazable-square-button-two")) {
    customElements.define(
      "gazable-square-button-two",
      GazableSquareButtonTwoElement
    );
  }

  var htmlTemplate3 =
    '<style>\r\n   :host {\r\n      display: inline-block;\r\n      border: var(--button-border, 1px solid transparent);\r\n      position: relative;\r\n      border-radius: var(--button-border-radius, 3pt);\r\n      box-sizing: border-box;\r\n      width: var(--button-width, 96px);\r\n      height: var(--button-height, 96px);\r\n      background-color: var(--button-dwell-background-color, rgba(56, 56, 56, 0.5));\r\n      padding: var(--button-dwell-thickness, 0.405vw); /* $square-button-height * 0.075; */\r\n      -webkit-tap-highlight-color: transparent;\r\n      user-select: none;\r\n   }\r\n\r\n   :host([disabled]) {\r\n      cursor: default;\r\n      pointer-events: none;\r\n      opacity: var(--button-disabled-opacity, 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) {\r\n      border-radius: var(--button-focused-border-radius, 0);\r\n      background: linear-gradient(\r\n         to left,\r\n         var(--button-dwell-background-color, rgba(56, 56, 56, 0.5)) 50%,\r\n         var(--button-dwell-color, #92bcff) 50%\r\n      );\r\n      background-size: 200% 200%;\r\n      background-repeat: repeat-y;\r\n      background-position: right bottom;\r\n   }\r\n\r\n   :host([gaze-activated]) {\r\n      border-radius: var(--button-border-radius, 3pt);\r\n   }\r\n\r\n   .inner-content {\r\n      display: flex;\r\n      justify-content: center;\r\n      align-items: center;\r\n      color: var(--button-text-color, #fff);\r\n      font-size: var(--button-font-size, 1vw);\r\n      font-weight: var(--button-font-weight, bold);\r\n      width: 100%;\r\n      height: 100%;\r\n      border-radius: var(--button-border-radius, 3px);\r\n      box-sizing: border-box;\r\n      font-family: var(--button-font-family, sans-serif);\r\n      box-shadow: var(\r\n         --button-inner-box-shadow,\r\n         inset 0px 0px 1px var(--button-inner-border-width, 1px) var(--button-inner-border-color, #fff)\r\n      );\r\n      background-color: var(--button-inner-background-color, #222);\r\n      text-align: center;\r\n      transition: box-shadow 200ms ease-out, border-radius 200ms ease-out;\r\n   }\r\n\r\n   :host([disabled]) .inner-content {\r\n      border-color: transparent;\r\n      background-color: var(--button-disabled-inner-color, #383838);\r\n      color: var(--button-disabled-text-color, #989899);\r\n   }\r\n\r\n   :host([gaze-activated]) .inner-content {\r\n      color: var(--button-activated-text-color, #111);\r\n      border-color: transparent;\r\n      box-shadow: var(\r\n            --button-activated-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         rgba(var(--button-activation-color-rgb, 146, 188, 255), 0.5);\r\n   }\r\n\r\n   :host([gaze-focused]) .inner-content,\r\n   :host(:hover) .inner-content {\r\n      border-color: var(--button-focused-inner-border-color, #92bcff);\r\n      box-shadow: var(\r\n            --button-focused-inner-box-shadow,\r\n            inset 0px 0px 1px var(--button-focused-inner-border-width, 3px)\r\n         )\r\n         var(--button-focused-border-color, #92bcff);\r\n      border-radius: var(--button-focused-inner-border-radius, 0);\r\n   }\r\n\r\n   .inner-content slot {\r\n      height: 100%;\r\n      width: 100%;\r\n      display: flex;\r\n      align-items: center;\r\n      justify-content: center;\r\n   }\r\n</style>\r\n\r\n<div class="inner-content">\r\n   <slot></slot>\r\n</div>\r\n';

  const gazableSquareButton3Template = document.createElement("template");
  gazableSquareButton3Template.innerHTML = htmlTemplate3;
  class GazableSquareButtonThreeElement extends GazableHTMLElement(
    HTMLElement
  ) {
    dwellAnimation = undefined;
    innerContent;
    activationColorRgb = "146, 188, 255";
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).append(
        gazableSquareButton3Template.content.cloneNode(true)
      );
      this.innerContent = this.shadowRoot?.querySelector(".inner-content");
    }
    startActivationAnimation() {
      const activationAnimation = this.animate(
        [
          { border: "0px" },
          {
            border:
              "var(--button-activation-border-width, 5px) solid transparent",
            backgroundColor: "transparent",
            offset: 0.5,
          },
          {
            border: "0px",
            backgroundColor: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
            offset: 0.75,
          },
          { backgroundColor: "transparent" },
        ],
        {
          duration: this.activationAnimationTime,
        }
      );
      this.setAttribute("gaze-activated", "");
      activationAnimation.oncancel = () =>
        this.removeAttribute("gaze-activated");
      activationAnimation.onfinish = () =>
        this.removeAttribute("gaze-activated");
      this.innerContent.animate(
        [
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), var(--button-activation-opacity, 0.3))`,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 0.3)`,
            offset: 0.05,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
            offset: 0.4,
          },
          {
            background: `rgba(var(--button-activation-color-rgb, ${this.activationColorRgb}), 1)`,
          },
        ],
        {
          duration: this.activationAnimationTime,
          easing: "ease",
          fill: "backwards",
        }
      );
    }
    onActivate() {
      this.startActivationAnimation();
      super.onActivate();
    }
    startDwellAnimation(dwellTime) {
      if (this.dwellAnimation !== undefined) this.dwellAnimation.cancel();
      this.dwellAnimation = this.animate(
        [
          { backgroundPosition: "right bottom" },
          { backgroundPosition: "left bottom" },
        ],
        {
          duration: dwellTime,
          iterations: 1,
          easing: "linear",
          fill: "none",
        }
      );
    }
    stopDwellAnimation() {
      this.dwellAnimation?.cancel();
      this.dwellAnimation = undefined;
    }
  }
  if (!customElements.get("gazable-square-button-three")) {
    customElements.define(
      "gazable-square-button-three",
      GazableSquareButtonThreeElement
    );
  }

  const GazableButtonElement = GazableHTMLElement(HTMLButtonElement);
  const GazableDivElement = GazableHTMLElement(HTMLDivElement);
  const GazableHeadElement = GazableHTMLElement(HTMLHeadElement);
  const GazableLinkElement = GazableHTMLElement(HTMLLinkElement);
  const GazableParagraphElement = GazableHTMLElement(HTMLParagraphElement);
  if (!customElements.get("a-gazable")) {
    customElements.define("a-gazable", GazableLinkElement, { extends: "a" });
  }
  if (!customElements.get("button-gazable")) {
    customElements.define("button-gazable", GazableButtonElement, {
      extends: "button",
    });
  }
  if (!customElements.get("div-gazable")) {
    customElements.define("div-gazable", GazableDivElement, { extends: "div" });
  }
  if (!customElements.get("head-gazable")) {
    customElements.define("head-gazable", GazableHeadElement, {
      extends: "head",
    });
  }
  if (!customElements.get("p-gazable")) {
    customElements.define("p-gazable", GazableParagraphElement, {
      extends: "p",
    });
  }

  exports.GazableButtonElement = GazableButtonElement;
  exports.GazableDivElement = GazableDivElement;
  exports.GazableElement = GazableElement;
  exports.GazableHeadElement = GazableHeadElement;
  exports.GazableParagraphElement = GazableParagraphElement;
  exports.GazableSquareButtonElement = GazableSquareButtonElement;

  Object.defineProperty(exports, "__esModule", { value: true });

  return exports;
})({});
